'use client';

import React, { useState, memo } from 'react';
import { ChevronDown, ChevronUp, CheckCircle, Flame, Info, TrendingUp } from 'lucide-react';
import { FaDiscord } from 'react-icons/fa6';
import MatrixGrid from '@/app/components/MatrixGrid';
import Link from 'next/link';

// ─── Rank SVG Icons ───────────────────────────────────────────────────────────

type GemPalette = { light: string; base: string; mid: string; dark: string; deep: string; pale: string };

const GOLD: GemPalette = { light: '#fef08a', base: '#facc15', mid: '#fde047', dark: '#d97706', deep: '#b45309', pale: '#fef9c3' };
const SILVER: GemPalette = { light: '#f4f4f5', base: '#d4d4d8', mid: '#e4e4e7', dark: '#71717a', deep: '#52525b', pale: '#fafafa' };

/** Faceted gem badge — shared by the Silver and Gold tiers, coloured by palette. */
const GemIcon = ({ id, c }: { id: string; c: GemPalette }) => (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id={`${id}Grad`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={c.mid} />
                <stop offset="50%" stopColor={c.base} />
                <stop offset="100%" stopColor={c.dark} />
            </linearGradient>
            <linearGradient id={`${id}Shine`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c.pale} stopOpacity="0.8" />
                <stop offset="100%" stopColor={c.base} stopOpacity="0" />
            </linearGradient>
            <filter id={`${id}Glow`}>
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
        </defs>
        {/* Outer gem shape */}
        <polygon points="32,4 52,18 52,46 32,60 12,46 12,18" fill={`url(#${id}Grad)`} filter={`url(#${id}Glow)`} />
        {/* Inner facets */}
        <polygon points="32,4 52,18 32,28" fill={c.light} opacity="0.5" />
        <polygon points="32,4 12,18 32,28" fill={c.mid} opacity="0.3" />
        <polygon points="32,28 52,18 52,46" fill={c.dark} opacity="0.4" />
        <polygon points="32,28 12,18 12,46" fill={c.deep} opacity="0.3" />
        <polygon points="32,28 52,46 32,60 12,46" fill={c.deep} opacity="0.3" />
        {/* Shine overlay */}
        <polygon points="32,4 52,18 32,28 12,18" fill={`url(#${id}Shine)`} opacity="0.6" />
        {/* Center sparkle */}
        <circle cx="32" cy="28" r="3" fill={c.pale} opacity="0.9" />
    </svg>
);

const PlatinumIcon = ({ variant = 1 }: { variant?: number }) => {
    const configs: Record<number, { primary: string; secondary: string; shine: string; id: string }> = {
        1: { primary: '#cbd5e1', secondary: '#94a3b8', shine: '#f1f5f9', id: 'plat1' },
        2: { primary: '#93c5fd', secondary: '#3b82f6', shine: '#dbeafe', id: 'plat2' },
        3: { primary: '#67e8f9', secondary: '#0891b2', shine: '#cffafe', id: 'plat3' },
        4: { primary: '#c084fc', secondary: '#7c3aed', shine: '#f3e8ff', id: 'plat4' },
        5: { primary: '#4ade80', secondary: '#059669', shine: '#dcfce7', id: 'plat5' },
    };
    const c = configs[variant] ?? configs[1];

    return (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id={`grad_${c.id}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={c.shine} />
                    <stop offset="50%" stopColor={c.primary} />
                    <stop offset="100%" stopColor={c.secondary} />
                </linearGradient>
                <linearGradient id={`shine_${c.id}`} x1="0" y1="0" x2="0.5" y2="1">
                    <stop offset="0%" stopColor="white" stopOpacity="0.7" />
                    <stop offset="100%" stopColor={c.primary} stopOpacity="0" />
                </linearGradient>
                <filter id={`glow_${c.id}`}>
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
            </defs>
            {/* Diamond shape — more Stake-like */}
            <polygon
                points="32,3 58,22 58,42 32,61 6,42 6,22"
                fill={`url(#grad_${c.id})`}
                filter={`url(#glow_${c.id})`}
            />
            {/* Top facet highlight */}
            <polygon points="32,3 58,22 32,30 6,22" fill={`url(#shine_${c.id})`} opacity="0.7" />
            {/* Left facet */}
            <polygon points="6,22 32,30 6,42" fill={c.secondary} opacity="0.25" />
            {/* Right facet */}
            <polygon points="58,22 32,30 58,42" fill={c.secondary} opacity="0.35" />
            {/* Bottom facet */}
            <polygon points="32,30 58,42 32,61 6,42" fill={c.secondary} opacity="0.2" />
            {/* Center gem glint */}
            <ellipse cx="32" cy="24" rx="5" ry="3" fill="white" opacity="0.5" transform="rotate(-20 32 24)" />
            <circle cx="28" cy="20" r="1.5" fill="white" opacity="0.8" />
        </svg>
    );
};

// ─── Config ───────────────────────────────────────────────────────────────────

const tiers = [
    {
        id: 'silver',
        label: 'SILVER',
        wager: 50000,
        prize: 25,
        color: 'from-zinc-200 via-zinc-300 to-zinc-500',
        borderColor: 'border-zinc-300/40',
        textColor: 'text-zinc-200',
        badgeBg: 'bg-zinc-300/10',
        rankIcon: <GemIcon id="silver" c={SILVER} />,
    },
    {
        id: 'gold',
        label: 'GOLD',
        wager: 100000,
        prize: 50,
        color: 'from-yellow-400 via-yellow-500 to-amber-600',
        borderColor: 'border-yellow-400/40',
        textColor: 'text-yellow-400',
        badgeBg: 'bg-yellow-400/10',
        rankIcon: <GemIcon id="gold" c={GOLD} />,
    },
    {
        id: 'platinum1',
        label: 'PLATINUM I',
        wager: 250000,
        prize: 100,
        color: 'from-slate-300 via-slate-400 to-slate-500',
        borderColor: 'border-slate-400/40',
        textColor: 'text-slate-300',
        badgeBg: 'bg-slate-400/10',
        rankIcon: <PlatinumIcon variant={1} />,
    },
    {
        id: 'platinum2',
        label: 'PLATINUM II',
        wager: 500000,
        prize: 175,
        color: 'from-slate-300 via-blue-300 to-slate-400',
        borderColor: 'border-blue-300/40',
        textColor: 'text-blue-300',
        badgeBg: 'bg-blue-300/10',
        rankIcon: <PlatinumIcon variant={2} />,
    },
    {
        id: 'platinum3',
        label: 'PLATINUM III',
        wager: 1000000,
        prize: 325,
        color: 'from-cyan-300 via-slate-400 to-cyan-500',
        borderColor: 'border-cyan-400/40',
        textColor: 'text-cyan-300',
        badgeBg: 'bg-cyan-400/10',
        rankIcon: <PlatinumIcon variant={3} />,
    },
    {
        id: 'platinum4',
        label: 'PLATINUM IV',
        wager: 2500000,
        prize: 750,
        color: 'from-purple-400 via-violet-500 to-purple-600',
        borderColor: 'border-purple-400/40',
        textColor: 'text-purple-300',
        badgeBg: 'bg-purple-400/10',
        rankIcon: <PlatinumIcon variant={4} />,
    },
    {
        id: 'platinum5',
        label: 'PLATINUM V',
        wager: 5000000,
        prize: 1500,
        color: 'from-green-400 via-emerald-500 to-green-600',
        borderColor: 'border-green-400/40',
        textColor: 'text-green-300',
        badgeBg: 'bg-green-400/10',
        rankIcon: <PlatinumIcon variant={5} />,
    },
];

const wagerRules = [
    {
        label: 'RTP ≤ 98%',
        example: 'Slots, Dice, most House Games',
        count: '100% counts',
        color: 'text-green-400',
        badge: 'bg-green-400/10',
    },
    {
        label: 'RTP > 98%',
        example: 'Some table games',
        count: '50% counts',
        color: 'text-green-400',
        badge: 'bg-green-400/10',
    },
    {
        label: 'RTP ≥ 99%',
        example: 'Baccarat, etc.',
        count: '10% counts',
        color: 'text-green-400',
        badge: 'bg-green-400/10',
    },
];

// ─── Tier Card ────────────────────────────────────────────────────────────────

const TierCard = memo(({ tier }: { tier: typeof tiers[0] }) => (
    <div className="relative h-full backdrop-blur rounded-2xl overflow-hidden transition-all duration-300 group">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        <div className="relative z-10 p-6 flex flex-col items-center text-center h-full">

            <div className="absolute top-4 right-4 z-20">
                <span className="bg-green-500 text-black text-xs font-mono font-bold px-3 py-1 rounded-full shadow-lg animate-pulse">
                    ONE CLAIM PER USER
                </span>
            </div>

            {/* Rank icon */}
            <div className="mb-3 mt-2 drop-shadow-xl">
                {tier.rankIcon}
            </div>

            <h3 className={`text-xl font-mono font-bold mb-4 bg-gradient-to-r ${tier.color} bg-clip-text text-transparent`}>
                {tier.label}
            </h3>

            <div className="bg-gradient-to-br from-emerald-600/20 via-emerald-800/20 to-gray-900/20 rounded-xl p-4 mb-4 w-full">
                <p className="text-xs text-gray-400 font-mono uppercase mb-1">Wager Required</p>
                <span className={`text-4xl font-bold bg-gradient-to-r ${tier.color} bg-clip-text text-transparent inline-block`}>
                    ${tier.wager.toLocaleString()}
                </span>
                <div className="mt-3 pt-3 border-t border-green-400/20">
                    <p className="text-2xl font-mono font-bold text-green-400">${tier.prize.toLocaleString()}</p>
                    <p className="text-xs text-gray-400 font-mono uppercase mt-1">Reward</p>
                </div>
            </div>

            <Link
                href="https://stake.com/?offer=sokoloco&c=c9bb838557"
                target="_blank"
                rel="noopener noreferrer"
                className="text-center bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 transition-all duration-200 px-8 py-3 font-mono font-bold text-gray-200 relative overflow-hidden w-full"
            >
                <span className="relative z-10">PLAY NOW →</span>
                <div className="absolute inset-0 bg-gradient-to-r from-green-400/0 via-green-400/30 to-green-400/0 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </Link>
        </div>
    </div>
));
TierCard.displayName = 'TierCard';

// ─── Rules Section ────────────────────────────────────────────────────────────

const RulesSection = memo(() => {
    return (
        <div className="w-full backdrop-blur shadow-sm rounded-2xl overflow-hidden border-b border-green-400/30 p-8">
            <h2 className="text-2xl md:text-4xl font-mono font-bold text-center mb-8 text-green-400">
                <span className="inline-block animate-pulse">{'['}</span> REWARD RULES <span className="inline-block animate-pulse">{']'}</span>
            </h2>

            <div className="space-y-8">

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {[
                        { step: '1', title: 'Register on Stake', desc: 'Make sure you are under a referral code: SOKOLOCO' },
                        { step: '2', title: 'Wager', desc: 'Hit the required wager threshold for a tier' },
                        { step: '3', title: 'Take Screenshot', desc: 'Capture proof of your level up' },
                        { step: '4', title: 'Open Ticket', desc: 'Create a Discord ticket to claim' }
                    ].map((item, idx) => (
                        <div key={idx} className="text-center group/step">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-500 rounded-full text-black text-xl font-bold mb-3 transition-transform duration-300 group-hover/step:scale-110 group-hover/step:rotate-12">
                                {item.step}
                            </div>
                            <h4 className="text-lg font-mono font-bold text-green-400 mb-2">{item.title}</h4>
                            <p className="text-sm text-gray-300 font-mono">{item.desc}</p>
                        </div>
                    ))}
                </div>

                <div className="border-t border-green-400/20 pt-8 grid grid-cols-1 md:grid-cols-3 gap-8">

                    <div>
                        <h3 className="text-green-400 font-mono font-bold mb-3 flex items-center gap-2">
                            <Flame className="w-4 h-4" /> GET STARTED
                        </h3>
                        <ul className="space-y-2">
                            {[
                                <span key="a">Wager on <span className="text-green-300 font-bold">Stake</span> under code <span className="text-green-300 font-bold">SOKOLOCO</span></span>,
                                'Wagering on house games is allowed.',
                                <span key="b">Wager counts from <span className="text-green-300 font-bold">03/03/2026</span> — previous wager does not count.</span>,
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-2 font-mono text-sm text-gray-300">
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-green-400 font-mono font-bold mb-3 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" /> REWARDS
                        </h3>
                        <ul className="space-y-2">
                            {[
                                'You can claim all tiers you qualify for.',
                                'Each reward is claimable once.',
                                'Rewards tipped to the Stake account that completed the wager.',
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-2 font-mono text-sm text-gray-300">
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-green-400 font-mono font-bold mb-3 flex items-center gap-2">
                            <Info className="w-4 h-4" /> WAGER COUNTING
                        </h3>
                        <div className="space-y-2">
                            {wagerRules.map((rule, i) => (
                                <div key={i} className={`flex items-center justify-between rounded-xl px-4 py-2.5 ${rule.badge}`}>
                                    <div>
                                        <p className={`font-mono font-bold text-sm ${rule.color}`}>{rule.label}</p>
                                        <p className="font-mono text-xs text-green-400/50">{rule.example}</p>
                                    </div>
                                    <span className={`font-mono font-bold text-sm ${rule.color}`}>{rule.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

                <div className="mt-8 pt-8 border-t border-green-400/20">
                    <div className="bg-green-500/10 rounded-xl p-6 text-center">
                        <p className="text-gray-300 font-mono text-sm">
                            After reaching a wager threshold, open a ticket in our Discord server to claim your reward. Our team will verify and tip you instantly.
                            Abusers will be <span className="text-gray-400 font-bold">disqualified</span> and have their prize forfeited.
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
});

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function StakeRewardsPage() {
    return (
        <div className="mt-15 text-gray-200 min-h-screen overflow-hidden relative">
            <MatrixGrid />
            <div className="flex flex-col items-center justify-center space-y-8 relative z-10 max-w-7xl mx-auto px-4 py-16">

                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-6xl font-mono font-bold tracking-widest mb-4">
                        <span className="bg-gradient-to-r from-green-400 via-emerald-300 to-green-500 bg-clip-text text-transparent">
                            STAKE LEVEL UP REWARDS
                        </span>
                    </h1>
                    <p className="uppercase text-green-300 font-mono text-sm md:text-base">
                        <span className="inline-block animate-pulse">{'['}</span> WAGER • LEVEL UP • CLAIM PRIZES <span className="inline-block animate-pulse">{']'}</span>
                    </p>
                </div>

                {/* Tier grid */}
                <div className="w-full mb-12">
                    <h2 className="text-2xl md:text-4xl font-mono font-bold text-center mb-8 text-green-400">
                        <span className="inline-block animate-pulse">{'['}</span> LEVEL UP TIERS <span className="inline-block animate-pulse">{']'}</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {tiers.map((tier, i) => {
                            // A lone card in the last row sits in the middle column (2- and 3-col layouts).
                            const isLast = i === tiers.length - 1;
                            const centerMd = isLast && tiers.length % 2 === 1;
                            const centerLg = isLast && tiers.length % 3 === 1;
                            return (
                                <div
                                    key={tier.id}
                                    className={['h-full',
                                        centerMd ? 'md:col-span-2 md:mx-auto md:w-[calc(50%-12px)]' : '',
                                        centerLg ? 'lg:col-span-1 lg:col-start-2 lg:mx-0 lg:w-auto' : centerMd ? 'lg:col-span-1 lg:mx-0 lg:w-auto' : '',
                                    ].join(' ')}
                                >
                                    <TierCard tier={tier} />
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Stake CTA */}
                <div className="text-center">
                    <Link
                        href="https://stake.com/?offer=sokoloco&c=c9bb838557"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 transition-all duration-200 px-8 py-4 font-mono font-bold text-gray-200 relative overflow-hidden"
                    >
                        <TrendingUp className="inline-block w-5 h-5 mr-2" />
                        START WAGERING ON STAKE
                    </Link>
                    {/* <p className="mt-4 text-green-300 font-mono text-sm text-center">
                        <span className="inline-block animate-pulse">
                            <small>Use code <strong>SOKOLOCO</strong> when registering. Only new accounts qualify.</small>
                        </span>
                    </p> */}
                </div>

                {/* Rules */}
                <RulesSection />

                {/* Discord CTA */}
                <div>
                    {/* CTA */}
                    <Link
                        href="https://discord.com/invite/sokoloco"
                        className="group flex items-center gap-3 px-4 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 hover: transition-all duration-200 px-8 py-4 font-mono font-bold text-gray-200 relative overflow-hidden">
                        <FaDiscord className="w-4 h-4 lg:w-6 lg:h-6" />
                        <span className="font-mono font-semibold">JOIN DISCORD NOW</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}